import React, { useState } from "react";
import "./ListenForSpecificInfoActivity.scss";
import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import ReloadIcon from "@rsuite/icons/Reload";
import { BiShow } from "react-icons/bi";
import ReactMarkdown from "react-markdown";
import { ButtonToolbar, IconButton } from "rsuite";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { CustomAudioPlayer } from "../../../shared/components/AudioPlayer/AudioPlayer";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import { extractBoldWords, formClass } from "../../../shared/helpers";
import { InputComponent } from "./components/InputComponent";

type Props = {
	data: {
		audio: string;
		text: string;
	};
	activityId: string;
};

const ListenForSpecifiInfoActivity: React.FC<Props> = ({
	data,
	activityId,
}) => {
	const originalWords = extractBoldWords(data.text);
	const prefixClass = "listen-for-specific-info";
	const { triggerActivityCompletion } = useActivityCompletion();

	const [words, setWords] = useState<
		{ word: string; correct: undefined | boolean }[]
	>([
		{
			word: "",
			correct: undefined,
		},
	]);

	const [fireworksTrigger, setFireworksTrigger] = useState(false);
	const [canPressCheck, setCanPressCheck] = useState(true);
	const [correctItems, setCorrectItems] = useState(0);
	const [showAnswers, setShowAnswers] = useState(false);

	const handelWordsChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number,
	) => {
		setWords((prev) => {
			const newWords = [...prev];
			newWords[index] = { word: e.target.value, correct: undefined };
			return newWords;
		});
	};

	const localIndexRef = React.useRef<number>(0);

	const strongRenderer = ({
		children,
	}: { children: string[] | React.ReactNode }) => {
		const word = Array.isArray(children) ? children[0] : "";
		const wordIndex = originalWords.indexOf(word);
		return (
			<InputComponent
				localIndexRef={localIndexRef}
				index={wordIndex}
				words={words}
				prefixClass={prefixClass}
				value={
					showAnswers ? originalWords[wordIndex] : words[wordIndex]?.word || ""
				}
				onChange={(e) => handelWordsChange(e, wordIndex)}
			/>
		);
	};

	const checkAnswersHandler = async () => {
		const doneWords = words.map((word, index) => {
			if (
				originalWords[index].toLowerCase().trim() ===
				word.word.toLowerCase().trim()
			) {
				return { ...word, correct: true };
			}
			return { ...word, correct: false };
		});

		setWords(doneWords);

		const allCorrect = doneWords.every((word) => word.correct === true);
		const onlyCorrect = doneWords.filter((word) => word.correct === true);
		if (allCorrect) {
			setFireworksTrigger(true);
			setCanPressCheck(false);
			await triggerActivityCompletion(activityId, 16, resetActivity);
		} else {
			setCanPressCheck(true);
		}
		setCorrectItems(onlyCorrect.length);
	};

	const resetActivity = () => {
		setWords(
			originalWords.map(() => {
				return { word: "", correct: undefined };
			}),
		);
		setCorrectItems(0);
		setCanPressCheck(true);
	};

	const handleFireworksComplete = () => {
		setFireworksTrigger(false);
	};

	return (
		<div className={formClass(prefixClass, "container")}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={handleFireworksComplete}
				/>
			)}
			<div className={formClass(prefixClass, "toolbar")}>
				<ButtonToolbar>
					<IconButton
						onClick={resetActivity}
						appearance="primary"
						icon={<ReloadIcon color="white" />}
					/>
					<IconButton
						disabled={!canPressCheck}
						onClick={checkAnswersHandler}
						appearance="primary"
						icon={<CheckOutlineIcon color="white" />}
					>
						Check
					</IconButton>
					<IconButton
						appearance="primary"
						icon={<BiShow color="white" />}
						onMouseDown={() => setShowAnswers(true)}
						onMouseUp={() => setShowAnswers(false)}
						onTouchStart={() => setShowAnswers(true)}
						onTouchEnd={() => setShowAnswers(false)}
					/>
				</ButtonToolbar>
				<div style={{ flex: 0.9 }}>
					{data?.audio && <CustomAudioPlayer src={data.audio} />}
				</div>
				<span className={formClass(prefixClass, "score")}>
					<strong>{`${correctItems} / ${words.length}`}</strong>
				</span>
			</div>
			<div className={formClass(prefixClass, "inner-container")}>
				<div className={formClass(prefixClass, "main-text")}>
					<ReactMarkdown
						className="main-text"
						components={{ strong: strongRenderer }}
					>
						{data.text}
					</ReactMarkdown>
				</div>
			</div>
		</div>
	);
};

export default ListenForSpecifiInfoActivity;
